import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AnalyticsQuery, VisualizationType } from '../../types/analytics.types';
import { useDashboards, useSaveCard, useCreateDashboard } from '../../hooks/useSavedCards';
import { useToast } from '@/hooks/use-toast';

interface SaveToDashboardModalProps {
  open: boolean;
  onClose: () => void;
  query: AnalyticsQuery;
  visualizationType: VisualizationType;
}

type RefreshFrequency = 'realtime' | 'hourly' | 'daily' | 'weekly';

const REFRESH_OPTIONS: Array<{ value: RefreshFrequency; label: string }> = [
  { value: 'realtime', label: 'Real-time' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

export function SaveToDashboardModal({
  open,
  onClose,
  query,
  visualizationType,
}: SaveToDashboardModalProps) {
  const [title, setTitle] = useState(query.title);
  const [selectedDashboard, setSelectedDashboard] = useState<string>('');
  const [newDashboardName, setNewDashboardName] = useState('');
  const [refreshFrequency, setRefreshFrequency] = useState<RefreshFrequency>('daily');
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const { data: dashboards, isLoading: dashboardsLoading } = useDashboards();
  const saveCard = useSaveCard();
  const createDashboard = useCreateDashboard();
  const { toast } = useToast();

  const handleSave = async () => {
    try {
      let dashboardId = selectedDashboard;

      if (isCreatingNew && newDashboardName) {
        const newDashboard = await createDashboard.mutateAsync(newDashboardName);
        dashboardId = newDashboard.id;
      }

      if (!dashboardId) {
        toast({
          title: 'Error',
          description: 'Please select or create a dashboard',
          variant: 'destructive',
        });
        return;
      }

      await saveCard.mutateAsync({
        title,
        query,
        visualizationType,
        dashboardId,
        position: { x: 0, y: 0, w: 4, h: 3 },
        refreshFrequency,
      });

      toast({
        title: 'Saved',
        description: 'Card has been saved to your dashboard',
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save card',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Save to Dashboard</DialogTitle>
          <DialogDescription>
            Save this visualization to a dashboard for quick access.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Card Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title for this card"
            />
          </div>

          <div className="grid gap-2">
            <Label>Dashboard</Label>
            <RadioGroup
              value={isCreatingNew ? 'new' : selectedDashboard}
              onValueChange={(value) => {
                if (value === 'new') {
                  setIsCreatingNew(true);
                  setSelectedDashboard('');
                } else {
                  setIsCreatingNew(false);
                  setSelectedDashboard(value);
                }
              }}
            >
              {dashboardsLoading ? (
                <div className="text-sm text-muted-foreground">Loading dashboards...</div>
              ) : (
                dashboards?.map((dashboard) => (
                  <div key={dashboard.id} className="flex items-center space-x-2">
                    <RadioGroupItem value={dashboard.id} id={dashboard.id} />
                    <Label htmlFor={dashboard.id} className="cursor-pointer">
                      {dashboard.name}
                      {dashboard.isDefault && (
                        <span className="text-xs text-muted-foreground ml-2">(Default)</span>
                      )}
                    </Label>
                  </div>
                ))
              )}
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new" className="cursor-pointer flex items-center gap-1">
                  <Plus className="h-4 w-4" />
                  Create new dashboard
                </Label>
              </div>
            </RadioGroup>

            {isCreatingNew && (
              <Input
                value={newDashboardName}
                onChange={(e) => setNewDashboardName(e.target.value)}
                placeholder="New dashboard name"
                className="mt-2"
              />
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="refresh">Refresh Frequency</Label>
            <Select
              value={refreshFrequency}
              onValueChange={(value) => setRefreshFrequency(value as RefreshFrequency)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFRESH_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!title || (!selectedDashboard && (!isCreatingNew || !newDashboardName))}
          >
            Save Card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
